import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input.jsx';

// Shared BNO form atoms + the RatingResults ladder table, extracted from
// routes/dev-bno-explorer.jsx so the BNO Quote Desk renders Global's rung
// ladders through the SAME component as the rating explorer (spec: "reuse
// the existing BNO rating explorer for the ladders"). One home; both
// surfaces import from here. Behavior is byte-for-byte the explorer's.

export const money = (v) => (v == null ? '' : `$${Number(v).toLocaleString('en-US')}`);

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-text-dim">{label}</span>
      {children}
      {hint ? <span className="block text-[10px] text-text-dim font-mono mt-0.5">{hint}</span> : null}
    </label>
  );
}

export function Txt({ value, onChange, ...rest }) {
  return <Input className="h-8 mt-0.5" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

export function Num({ value, onChange, ...rest }) {
  return (
    <Input className="h-8 mt-0.5" type="number" value={value ?? ''} {...rest}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
  );
}

export function Sel({ value, onChange, options, allowEmpty }) {
  return (
    <select className="h-8 mt-0.5 w-full rounded-md border border-border bg-bg px-2 text-sm"
      value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
      {allowEmpty ? <option value="">(none)</option> : null}
      {options.map((o) => (typeof o === 'object'
        ? <option key={o.value} value={o.value}>{o.label}</option>
        : <option key={o} value={o}>{o}</option>))}
    </select>
  );
}

export function Toggle({ checked, onChange, label, hint }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer">
      <input type="checkbox" className="h-4 w-4 accent-accent" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-text">{label}</span>
      {hint ? <span className="text-[10px] text-text-dim font-mono">{hint}</span> : null}
    </label>
  );
}

export function Group({ title, children, defaultOpen = true, right = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg bg-surface">
      <div className="w-full flex items-center justify-between pr-3">
        <button type="button" onClick={() => setOpen(!open)}
          className="flex-1 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-text text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {title}
        </button>
        {right}
      </div>
      {open ? <div className="px-3 pb-3 space-y-2">{children}</div> : null}
    </div>
  );
}

export function LadderTable({ title, rows, limitLabel, selectedIdx, onSelect, renderLimit }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-text-dim mb-1">{title}</div>
      <div className="overflow-x-auto border border-border rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-surface-strong text-text-dim">
              <th className="text-left px-2 py-1 font-medium">{limitLabel}</th>
              <th className="text-right px-2 py-1 font-medium">Premium</th>
              <th className="text-right px-2 py-1 font-medium">Delta</th>
              <th className="text-left px-2 py-1 font-medium">RatingOptionIdentifier</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const selected = selectedIdx === i;
              const selPremium = selectedIdx != null && rows[selectedIdx] ? rows[selectedIdx].premium : null;
              const delta = selPremium != null && r.premium != null ? r.premium - selPremium : null;
              return (
                <tr key={r.ratingOptionId || i} onClick={() => onSelect(i)}
                  className={`cursor-pointer border-t border-border/60 ${selected ? 'bg-info-soft font-semibold' : 'hover:bg-surface-strong'}`}>
                  <td className="px-2 py-1">{renderLimit(r)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(r.premium)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-text-dim">
                    {delta == null || delta === 0 ? '' : `${delta > 0 ? '+' : ''}${delta.toLocaleString('en-US')}`}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-text-dim">{(r.ratingOptionId || '').slice(0, 8)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
